/**
 * Pure helpers for Routy's reassign_appointment tool (86bcptebv): decide
 * whether a reassignment may proceed, whether the new assignee is off the
 * appointment's booking form, and the copy for that case. No API calls here -
 * the executor in routyTools.ts does the I/O.
 */

export type ReassignRefusal =
  | { kind: 'multipleAssignees' }
  | { kind: 'alreadyAssigned' }
  | { kind: 'notAssignable'; name: string };

/** Decide whether a reassignment may proceed, before any availability check. */
export function checkReassignment(args: {
  currentAssigneeIds: string[];
  newUserId: string;
  assignability: { name: string; assignable: boolean } | null;
}): ReassignRefusal | null {
  const { currentAssigneeIds, newUserId, assignability } = args;
  const unique = Array.from(new Set(currentAssigneeIds.filter((id) => !!id)));
  if (unique.length > 1) return { kind: 'multipleAssignees' };
  if (unique.length === 1 && unique[0] === newUserId) return { kind: 'alreadyAssigned' };
  // An unknown id (null) is left to the backend to judge.
  if (assignability && !assignability.assignable) return { kind: 'notAssignable', name: assignability.name };
  return null;
}

/**
 * True when the appointment has a booking form and the new user is not on its
 * roster. An unknown roster (null - the fetch failed) is never reported as off-form.
 */
export function isOffForm(
  bookingFormId: string | null | undefined,
  formUserIds: string[] | null,
  newUserId: string,
): boolean {
  if (!bookingFormId || formUserIds === null) return false;
  return !formUserIds.includes(newUserId);
}

/** Card line / result copy for an off-form assignee. */
export function offFormCardText(name: string, formName: string): string {
  return (
    `${name} isn't on the “${formName}” booking form — they'll get this appointment only; ` +
    "the form's team and customer self-booking are unchanged."
  );
}
